// src/components/about/StoryTimeline.js
import React, { useEffect, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';
import { FaRegCalendarAlt, FaUsers, FaLaptopCode, FaAward, FaHandshake, FaGraduationCap } from 'react-icons/fa';
import gsap from 'gsap';
import Heading from '../common/Heading';
import Section from '../common/Section';

const StoryTimeline = () => {
  // Timeline events
  const timelineEvents = [
    {
      id: 1,
      date: 'November 2020',
      title: 'Establishment',
      description: 'Leotech was established to equip STEM youths with "Skills Against Poverty," focusing on the role of adequately skilled youths in poverty alleviation.',
      icon: <FaRegCalendarAlt />,
      iconColor: 'bg-blue-200 text-primary-dark',
      image: '/images/about/timeline/leotech-logo.png',
    },
    {
      id: 2,
      date: 'January 19, 2021',
      title: 'Official Registration',
      description: 'Leotech formally registered and incorporated with the Corporate Affairs Commission under the Companys Registration Act of Sierra Leone.',
      icon: <FaAward />,
      iconColor: 'bg-secondary text-white',
      image: '/images/about/timeline/registration.jpg',
    },
    {
      id: 3,
      date: 'February 2021',
      title: 'First Cohort',
      description: 'The first training cohort began with 20 students focusing on Computer-Aided Design and Building Information',
      icon: <FaGraduationCap />,
      iconColor: 'bg-yellow-200 text-yellow-800',
      image: '/images/about/timeline/first-cohort.jpg',
    },
    {
      id: 4,
      date: 'August 2021',
      title: 'Partnership with Engineers Without Borders',
      description: 'Formed a strategic partnership with Engineers Without Borders - Denmark to expand training opportunities.',
      icon: <FaHandshake />,
      iconColor: 'bg-green-200 text-green-800',
      image: '/images/about/timeline/partnership.jpg',
    },
    {
      id: 5,
      date: 'December 2022',
      title: 'Reached 300 Trainees',
      description: 'Successfully trained over 300 youths across multiple cohorts in various digital skills programs.',
      icon: <FaUsers />,
      iconColor: 'bg-indigo-200 text-indigo-800',
      image: '/images/about/timeline/trainees.jpg',
    },
    {
      id: 6,
      date: 'April 2023',
      title: 'Expanded Program Offerings',
      description: 'Added Land Survey & Photogrammetry and Computer Programming to the training curriculum.',
      icon: <FaLaptopCode />,
      iconColor: 'bg-purple-200 text-purple-800',
      image: '/images/about/timeline/programs.jpg',
    },
    {
      id: 7,
      date: 'January 2025',
      title: 'Milestone Achievement',
      description: 'Reached the milestone of training over 700 youths across 12 cohorts and building a community of 3,000+ youth across Sierra Leone.',
      icon: <FaAward />,
      iconColor: 'bg-secondary text-white',
      image: '/images/about/timeline/milestone.jpg',
    },
  ];
  
  // Animation controls and refs
  const controls = useAnimation();
  const timelineRef = useRef(null);
  const isInView = useInView(timelineRef, { once: true, amount: 0.2 });
  
  // Start animation when timeline comes into view
  useEffect(() => {
    if (isInView) {
      controls.start('visible');
      
      // Animate the timeline line using GSAP
      gsap.fromTo(
        '.timeline-line',
        { scaleY: 0 },
        { scaleY: 1, duration: 1.5, ease: 'power3.out' }
      );
    }
  }, [isInView, controls]);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.5
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  // Function to get image URL with fallback
  const getImageUrl = (event) => {
    // Try to load the actual image first
    return event.image || `https://placehold.co/300x300/1a237e/ffffff?text=${encodeURIComponent(event.title)}`;
  };
  
  // Function to handle image loading errors
  const handleImageError = (e, title) => {
    e.target.src = `https://placehold.co/300x300/1a237e/ffffff?text=${encodeURIComponent(title)}`;
  };
  
  return (
    <Section background="gray" padding="lg">
      <div className="text-center mb-12">
        <Heading level={2} align="center" className="mb-4" withAccent>
          Our Journey
        </Heading>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Since our founding in 2020, Leotech has evolved to become a leading provider of digital skills training in Sierra Leone
        </p>
      </div>
      
      {/* Timeline Container */}
      <div className="max-w-5xl mx-auto relative" ref={timelineRef}>
        {/* Vertical Timeline Line */}
        <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gray-200 timeline-line origin-top"></div>
        
        <motion.div
          className="space-y-12"
          variants={containerVariants}
          initial="hidden"
          animate={controls}
        >
          {timelineEvents.map((event, index) => (
            <motion.div 
              key={event.id}
              variants={itemVariants}
              className={`flex flex-col ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
              } items-center`}
            >
              {/* Content Box */}
              <div className="md:w-1/2 p-6 z-10">
                <div 
                  className={`bg-white rounded-lg shadow-lg p-6 border-l-4 ${
                    index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'
                  } border-primary transform transition-transform duration-300 hover:-translate-y-2`}
                >
                  {/* Image section - always renders with fallback */}
                  <div className="mb-4 overflow-hidden rounded-lg">
                    <img 
                      src={getImageUrl(event)}
                      alt={event.title} 
                      className="w-full h-48 object-cover transform transition-transform duration-300 hover:scale-105"
                      onError={(e) => handleImageError(e, event.title)}
                    />
                  </div>
                  <div className="text-secondary font-medium mb-2">{event.date}</div>
                  <h3 className="text-xl font-bold text-primary-dark mb-3">{event.title}</h3>
                  <p className="text-gray-600">{event.description}</p>
                </div>
              </div>
              
              {/* Timeline Node */}
              <div className="md:w-0 flex justify-center items-center z-20">
                <div
                  className={`flex items-center justify-center w-12 h-12 rounded-full ${event.iconColor} border-4 border-white shadow-lg text-xl transform transition-transform duration-300 hover:scale-110`}
                >
                  {event.icon}
                </div>
              </div>
              
              {/* Empty Space for Alternating Layout */}
              <div className="md:w-1/2 hidden md:block"></div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </Section>
  );
};

export default StoryTimeline;